import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Upload, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AssetLibrary = () => {
  const { t } = useLanguage();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const { data } = await axios.get(`${API}/assets`, { withCredentials: true });
      setAssets(data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/assets/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchAssets();
    } catch (error) {
      console.error('Upload failed:', error);
      alert(t('Upload failed', 'فشل التحميل'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-3xl font-black tracking-tight text-[#EDEDED]">
            {t('Asset Library', 'مكتبة الأصول')}
          </h2>
          <p className="text-[#A1A1AA] mt-1">
            {t('Manage your anime safety images', 'إدارة صور السلامة الخاصة بك')}
          </p>
        </div>

        <label>
          <input
            type="file"
            data-testid="upload-asset-input"
            className="hidden"
            accept="image/*"
            onChange={handleUpload}
            disabled={uploading}
          />
          <Button data-testid="upload-asset-button" asChild className="bg-safety-amber hover:bg-safety-amber/90 text-obsidian font-bold">
            <span className="cursor-pointer">
              <Upload className="w-4 h-4 me-2" />
              {uploading ? t('Uploading...', 'جارٍ التحميل...') : t('Upload Image', 'تحميل صورة')}
            </span>
          </Button>
        </label>
      </div>

      {loading ? (
        <div className="text-center text-[#A1A1AA] py-12">{t('Loading...', 'تحميل...')}</div>
      ) : assets.length === 0 ? (
        <div className="text-center py-20 bg-carbon border border-[#27272A] rounded-lg">
          <ImageIcon className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">{t('No assets uploaded yet', 'لم يتم تحميل أي أصول بعد')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {assets.map(asset => (
            <div
              key={asset.id}
              data-testid={`asset-${asset.id}`}
              className="bg-carbon border border-[#27272A] rounded-lg overflow-hidden hover:border-mecha-blue/30 transition-all"
            >
              <div className="aspect-square bg-obsidian flex items-center justify-center">
                <ImageIcon className="w-12 h-12 text-[#52525B]" />
              </div>
              <div className="p-3">
                <p className="text-sm text-[#EDEDED] truncate">{asset.filename}</p>
                <p className="text-xs text-[#A1A1AA] mt-1">{(asset.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssetLibrary;
